import { useTranslations } from '@lang';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import type { JourneyPickerLabels, JourneyPickerSearchParams } from 'voyager';
import { JourneyPicker } from 'voyager';
import type { OnChangeParams } from '../JourneyPicker/JourneyPicker.types';
import { CenterWrapper } from './JourneyPickerV2.styles';
import type { JourneyPickerV2Props } from './JourneyPickerV2.types';

export type {
  DateContext,
  JourneyPickerSearchParams,
  JourneyPickerV2Props,
} from './JourneyPickerV2.types';

function useJourneyPickerLabels(): JourneyPickerLabels {
  const { t } = useTranslations();

  return useMemo(() => {
    const localeData = moment.localeData();
    const weekdays = localeData.weekdaysMin();
    const mondayFirst = [...weekdays.slice(1), weekdays[0]];
    const months = localeData.months();

    return {
      selectDate: t('spa.selectDate'),
      selectDates: t('common.dates'),
      search: t('spaClasses.search'),
      browse: t('common.category'),
      guests: (total: number) => t('common.numberOfGuests', { guestsAmount: total }),
      datePicker: {
        weekdays: mondayFirst,
        months,
        date: t('common.date'),
        checkIn: t('guestsPicker.checkin'),
        checkOut: t('guestsPicker.checkout'),
        selectDate: t('spa.selectDate'),
        previousMonth: t('aria.previousMonth'),
        nextMonth: t('aria.nextMonth'),
      },
      guestPicker: {
        guests: t('guestsPicker.label'),
        adults: t('guestsPicker.adultsLabel'),
        children: t('guestsPicker.childrenLabel'),
        summary: (adults: number, children: number) =>
          t('guestsPicker.value', {
            numberOfAdults: adults,
            numberOfChildren: children,
          }),
      },
    };
  }, [t]);
}

export function JourneyPickerV2({
  dateMode = 'range',
  dateContext = 'stay',
  showGuests,
  categories,
  category,
  onCategoryChange,
  disableCategorySelect,
  journeyParams,
  changeJourneyParam,
  onChange,
  onSearchComplete,
  columnView,
  numberOfMonths,
}: JourneyPickerV2Props) {
  const labels = useJourneyPickerLabels();

  const defaultFromDate =
    dateContext === 'spa' || dateContext === 'dining'
      ? journeyParams.spaDate?.format('YYYY-MM-DD') ||
        journeyParams.fromDate?.format('YYYY-MM-DD') ||
        ''
      : journeyParams.fromDate?.format('YYYY-MM-DD') || '';

  const defaultToDate = journeyParams.toDate?.format('YYYY-MM-DD') || '';

  const defaultSpaDate = journeyParams.spaDate?.format('YYYY-MM-DD') || '';

  const handleSearch = useCallback(
    (params: JourneyPickerSearchParams) => {
      if (dateContext === 'spa') {
        changeJourneyParam('spaAdults', params.adults);
      } else {
        changeJourneyParam('adults', params.adults);
        changeJourneyParam('children', params.children);
      }
      changeJourneyParam('fromDate', params.fromDate);
      changeJourneyParam('spaDate', params.fromDate);
      changeJourneyParam('diningDate', params.fromDate);
      if (params.toDate) {
        changeJourneyParam('toDate', params.toDate);
      } else if (
        defaultToDate &&
        moment(params.fromDate).isSameOrAfter(moment(defaultToDate), 'day')
      ) {
        changeJourneyParam('toDate', moment(params.fromDate).add(2, 'days').format());
      }

      if (onChange) {
        const changeParams: OnChangeParams = {
          adults: params.adults ?? journeyParams.adults,
          children: params.children ?? journeyParams.children,
          fromDate: dateContext === 'stay' ? params.fromDate : defaultFromDate,
          toDate: dateContext === 'stay' ? params.toDate : defaultToDate,
          spaDate: dateContext === 'spa' ? params.fromDate : defaultSpaDate,
        };
        onChange(changeParams);
      }

      onSearchComplete?.(params);
    },
    [
      dateContext,
      changeJourneyParam,
      onChange,
      onSearchComplete,
      journeyParams.adults,
      journeyParams.children,
      defaultFromDate,
      defaultToDate,
      defaultSpaDate,
    ]
  );

  return (
    <CenterWrapper>
      <JourneyPicker
        dateMode={dateMode}
        showGuests={showGuests}
        categories={categories}
        category={category}
        onCategoryChange={onCategoryChange}
        disableCategorySelect={disableCategorySelect}
        defaultFromDate={defaultFromDate}
        defaultToDate={dateMode === 'range' ? defaultToDate : undefined}
        defaultAdults={dateContext === 'spa' ? journeyParams.spaAdults : journeyParams.adults}
        defaultChildren={journeyParams.children}
        columnView={columnView}
        numberOfMonths={numberOfMonths}
        onSearch={handleSearch}
        labels={labels}
      />
    </CenterWrapper>
  );
}
